import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Box, Paper, useTheme, alpha, useMediaQuery } from '@mui/material';
import { Button, IconButton, Loader, Typography } from '@sm-dashboard/ui-components';
import { useNavigate } from 'react-router-dom';
import { useFetch } from '@/hooks/useFetch';
import { getPersonalPagesUrl, ticketsInfoUrl } from '@/constants';
import {
  PersonalPage,
  TicketInfo,
  Ticket,
  resolveEventTotalCapacity,
  resolvePageCapabilities
} from '@sm-dashboard/types';
import instanceAxios from '@/util/axios';
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined';
import EventIcon from '@mui/icons-material/Event';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { combineDateAndTime, resolveEventDateTimeFromEventData } from '@sm-dashboard/formatting';

interface EventStatistics {
  ticketsSold: number;
  totalSales: number;
}

type PublishedEventData = { date?: Date | string | null; time?: Date | string | null };

const resolveEventDateTimeForCrm = (event: PersonalPage): Date | null => {
  const published = (event as unknown as { publishedEventData?: PublishedEventData }).publishedEventData;
  const fromPublished = combineDateAndTime(published?.date ?? null, published?.time ?? null);
  if (fromPublished) {
    return fromPublished;
  }
  return resolveEventDateTimeFromEventData(event.eventData);
};

const isEventActive = (event: PersonalPage): boolean => {
  if (!event.isPublished || !event.isActive) {
    return false;
  }

  const now = new Date();
  const eventDateTime = resolveEventDateTimeForCrm(event);
  if (eventDateTime && eventDateTime < now) {
    return false;
  }

  if (event.isActiveInTimeRange && event.activeDate && event.expiryDate) {
    const activeDate = new Date(event.activeDate);
    const expiryDate = new Date(event.expiryDate);
    if (now < activeDate || now > expiryDate) {
      return false;
    }
  }

  return true;
};

const calculateEventStatistics = (event: PersonalPage, ticketsInfoFromApi: TicketInfo[]): EventStatistics => {
  if (!event._id) {
    return { ticketsSold: 0, totalSales: 0 };
  }
  const eventTicketsFromEvent = Array.isArray((event as PersonalPage & { ticketsInfo?: TicketInfo[] }).ticketsInfo)
    ? ((event as PersonalPage & { ticketsInfo?: TicketInfo[] }).ticketsInfo as TicketInfo[])
    : [];
  const eventTicketsFromApi = ticketsInfoFromApi.filter(ticket => {
    const eventIdFromTicket = String(ticket.eventId);
    const eventIdFromEvent = String(event._id);
    return (
      eventIdFromTicket === eventIdFromEvent &&
      (ticket.status === 'paid' || ticket.status === 'pending' || !ticket.status)
    );
  });
  const eventTickets = (eventTicketsFromEvent.length > 0 ? eventTicketsFromEvent : eventTicketsFromApi).filter(
    ticket => ticket.status === 'paid' || ticket.status === 'pending' || !ticket.status
  );
  const ticketsSold = eventTickets.reduce((sum, ticket) => sum + (ticket.quantity || 0), 0);
  const totalSales = eventTickets.reduce((sum, ticket) => {
    const amount =
      typeof ticket.purchaseAmount === 'string' ? parseFloat(ticket.purchaseAmount) : ticket.purchaseAmount || 0;
    return sum + amount;
  }, 0);

  return {
    ticketsSold,
    totalSales
  };
};

const MOBILE_EVENTS_VISIBLE = 2;

const MyEventsList: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isMobileList = useMediaQuery(theme.breakpoints.down('md'));
  const [eventsListExpanded, setEventsListExpanded] = useState(false);
  const navigate = useNavigate();
  const { loading: loadingEvents, fetchData: fetchEvents, data: eventsData } = useFetch();
  const [ticketsInfo, setTicketsInfo] = useState<TicketInfo[]>([]);
  const [loadingTickets, setLoadingTickets] = useState(false);

  const handleViewEvent = useCallback(
    (event: PersonalPage) => {
      if (!event._id) {
        return;
      }
      navigate(`/org/event-sales/${event._id}`);
    },
    [navigate]
  );

  useEffect(() => {
    const params = new URLSearchParams({
      rowsPerPage: '1000',
      pageType: 'event',
      forceShaliachId: 'true'
    });

    const url = `${getPersonalPagesUrl}?${params.toString()}`;

    fetchEvents({
      method: 'GET',
      url
    });
  }, [fetchEvents]);

  useEffect(() => {
    const loadTicketsInfo = async () => {
      setLoadingTickets(true);
      try {
        const response = await instanceAxios.get(ticketsInfoUrl);
        setTicketsInfo(response.data || []);
      } catch (error) {
        console.error('Error fetching tickets info:', error);
      } finally {
        setLoadingTickets(false);
      }
    };

    loadTicketsInfo();
  }, []);

  const events: PersonalPage[] = useMemo(() => {
    if (!eventsData?.rows) {
      return [];
    }
    return eventsData.rows.filter((event: PersonalPage) => isEventActive(event));
  }, [eventsData]);

  const totalStats = useMemo(() => {
    let totalRegistrations = 0;
    let totalSales = 0;

    events.forEach(event => {
      const stats = calculateEventStatistics(event, ticketsInfo);
      totalRegistrations += stats.ticketsSold;
      totalSales += stats.totalSales;
    });

    return {
      registrations: totalRegistrations,
      sales: totalSales
    };
  }, [events, ticketsInfo]);

  const loadingEventsList = loadingEvents || loadingTickets;

  const visibleEvents = useMemo(() => {
    if (!isMobileList || eventsListExpanded) {
      return events;
    }
    return events.slice(0, MOBILE_EVENTS_VISIBLE);
  }, [events, isMobileList, eventsListExpanded]);

  const showEventsExpandControl = isMobileList && events.length > MOBILE_EVENTS_VISIBLE;

  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box
        sx={{
          bgcolor: 'success.main',
          color: 'common.white',
          p: 1,
          pl: 2,
          borderRadius: '30px 30px 0 0',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexDirection: 'row'
        }}
      >
        <Box
          sx={{
            width: 12,
            height: 12,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <EventIcon sx={{ fontSize: 20 }} />
        </Box>
        <Box sx={{ flex: 1, textAlign: 'left' }}>
          <Typography variant="h6">האירועים שלי</Typography>
        </Box>
      </Box>

      <Paper
        sx={{
          borderRadius: '0 0 20px 20px',
          overflow: 'hidden',
          bgcolor: alpha(theme.palette.grey[300], 0.5),
          display: 'flex',
          flexDirection: 'column',
          minHeight: {
            xs: isMobileList && !eventsListExpanded ? 'auto' : 280,
            sm: 360
          },
          height: { xs: 'auto', sm: 360 },
          maxHeight: { xs: isMobileList && eventsListExpanded ? 400 : 'none', sm: 'none' },
          p: { xs: 1, sm: 2 }
        }}
      >
        <Box
          sx={{
            p: { xs: 1.5, sm: 3 },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-evenly',
            gap: { xs: 2, sm: 4 },
            bgcolor: theme.palette.grey[200],
            overflow: 'hidden'
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Box sx={{ mb: 0.5 }}>
              <Typography variant={isMobileList ? 'body' : 'h3'} color="primary.main">
                סכום מכירות
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography variant={isMobileList ? 'body' : 'h3'} color="primary.main">
                {totalStats.sales.toLocaleString('he-IL')}
              </Typography>
              <Typography variant={isMobileList ? 'body' : 'h3'} color="primary.main">
                ₪
              </Typography>
            </Box>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center'
            }}
          >
            <Box sx={{ mb: 0.5 }}>
              <Typography variant={isMobileList ? 'body' : 'h3'} color="primary.main">
                מספר כרטיסים
              </Typography>
            </Box>
            <Typography variant={isMobileList ? 'body' : 'h3'} color="primary.main">
              {totalStats.registrations.toLocaleString('he-IL')}
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            flex: isMobileList && !eventsListExpanded ? 'none' : 1,
            overflowY: isMobileList && !eventsListExpanded ? 'hidden' : 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            minHeight: 0
          }}
        >
          {loadingEventsList ? (
            <Box sx={{ py: 4, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <Loader size={32} thickness={5} />
            </Box>
          ) : events.length === 0 ? (
            <Box
              sx={{
                textAlign: 'center',
                py: 4,
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <Box sx={{ color: 'text.secondary' }}>
                <Typography variant="body">אין אירועים להצגה</Typography>
              </Box>
            </Box>
          ) : (
            visibleEvents.map(event => {
              const stats = calculateEventStatistics(event, ticketsInfo);
              const eventCapabilities = resolvePageCapabilities(event);
              const hasItemPayments = eventCapabilities.itemOptions.payments;
              const publishedTickets = ((event as unknown as { publishedTickets?: Ticket[] }).publishedTickets ??
                []) as Ticket[];
              const totalCapacity = resolveEventTotalCapacity({
                capabilities: eventCapabilities,
                event: {
                  isUnlimitedRegistration: event.isUnlimitedRegistration,
                  registrationLimit: event.registrationLimit,
                  tickets: publishedTickets
                }
              });

              return (
                <Box
                  key={event._id || event.id}
                  onClick={() => handleViewEvent(event)}
                  sx={{
                    px: { xs: 1.5, sm: 2 },
                    py: 1.25,
                    borderRadius: 1,
                    bgcolor: 'common.white',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    flexDirection: 'row-reverse',
                    '&:hover': {
                      bgcolor: alpha(theme.palette.grey[50], 0.8),
                      transform: 'translateX(-2px)'
                    }
                  }}
                >
                  <Box sx={{ flexShrink: 0, alignSelf: isMobile ? 'center' : 'auto' }}>
                    <IconButton
                      color="primary"
                      shape="circle"
                      size="small"
                      aria-label="צפייה באירוע"
                      onClick={e => {
                        e.stopPropagation();
                        handleViewEvent(event);
                      }}
                    >
                      <ArrowForwardIosIcon sx={{ fontSize: 15, transform: 'rotate(180deg)' }} />
                    </IconButton>
                  </Box>
                  <Box
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      alignItems: { xs: 'flex-start', sm: 'flex-start' },
                      gap: { xs: 0.5, sm: 2 }
                    }}
                  >
                    <Box sx={{ width: { xs: '100%', sm: '30%' }, flexShrink: 0, overflow: 'hidden' }}>
                      <Box
                        sx={{
                          textAlign: 'left',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        <Typography variant="subtitle2" color="text.primary">
                          {(event as any).name || event.title || 'שם האירוע שאתם עושים'}
                        </Typography>
                      </Box>
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: { xs: 1.5, sm: 2 },
                        width: { xs: '100%', sm: 'auto' },
                        flex: { sm: 1 }
                      }}
                    >
                      {totalCapacity > 0 ? (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            overflow: 'hidden',
                            minWidth: 0
                          }}
                        >
                          <ConfirmationNumberOutlinedIcon sx={{ fontSize: 18, color: 'success.main', flexShrink: 0 }} />
                          <Box
                            sx={{
                              color: 'text.secondary',
                              fontSize: { xs: '0.75rem', sm: '0.875rem' },
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              minWidth: 0
                            }}
                          >
                            <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
                              {stats.ticketsSold.toLocaleString('he-IL')}
                            </Box>
                            {' מתוך '}
                            <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
                              {totalCapacity.toLocaleString('he-IL')}
                            </Box>
                          </Box>
                        </Box>
                      ) : (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            overflow: 'hidden',
                            minWidth: 0
                          }}
                        >
                          <ConfirmationNumberOutlinedIcon sx={{ fontSize: 18, color: 'success.main', flexShrink: 0 }} />
                          <Box
                            sx={{
                              color: 'text.secondary',
                              fontSize: { xs: '0.75rem', sm: '0.875rem' },
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              minWidth: 0
                            }}
                          >
                            <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
                              {stats.ticketsSold.toLocaleString('he-IL')}
                            </Box>
                            {' הרשמות'}
                          </Box>
                        </Box>
                      )}
                      {hasItemPayments && (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            overflow: 'hidden',
                            minWidth: 0
                          }}
                        >
                          <Box
                            component="span"
                            sx={{
                              fontWeight: 700,
                              color: 'success.main',
                              fontSize: { xs: '0.875rem', sm: '1rem' },
                              flexShrink: 0,
                              lineHeight: 1
                            }}
                          >
                            ₪
                          </Box>
                          <Box
                            sx={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              minWidth: 0
                            }}
                          >
                            <Typography variant="body" color="text.secondary">
                              {stats.totalSales.toLocaleString('he-IL')}
                            </Typography>
                          </Box>
                        </Box>
                      )}
                    </Box>
                  </Box>
                </Box>
              );
            })
          )}
        </Box>
        {showEventsExpandControl && (
          <Box sx={{ pt: 1, pb: 0.5, px: 0.5, flexShrink: 0 }}>
            <Button fullWidth color="warning" variant="contained" onClick={() => setEventsListExpanded(prev => !prev)}>
              {eventsListExpanded ? 'הצג פחות' : `הצג את כל האירועים (${events.length})`}
            </Button>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default MyEventsList;
